import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import axios, { AxiosError, AxiosResponse } from "axios";
import { IConfig } from "..";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { removeNullKeys } from "../../../utils/formatters";
import { useAuth } from "../../../hooks/auth";

interface IUserModalProps {
  visible: boolean;
  handleCloseModal: (config?: IConfig[]) => void;
  config?: IConfig;
}

interface IUserFormData {
  name: string;
  value: number;
  user_id: string;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const ConfigEditModal: React.FC<IUserModalProps> = ({
  visible,
  handleCloseModal,
  config,
}) => {
  const { t } = useTranslation(["config"]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { addToast } = useToast();
  const [form] = Form.useForm();

  const { user } = useAuth();

  const isEditing = useMemo(() => !!config, [config]);

  useEffect(() => {
    form.setFieldsValue({
      ...config,
    });
  }, [form, config]);

  const handleClose = useCallback(
    (usr?: IConfig[]) => {
      form.resetFields();
      handleCloseModal(usr);
    },
    [form, handleCloseModal]
  );

  const formatDate = useCallback((date: any) => {
    if (!date) {
      return undefined;
    }
    return date.format("YYYY-MM-DD");
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O usuário não foi encontrado";
          break;
        default:
          description = isEditing
            ? "Ocorreu um erro ao alterar os dados do equipamento, tente novamente"
            : "Ocorreu um erro ao criar o usuario, tente novamente";
      }

      addToast({
        type: "error",
        title: isEditing
          ? t("requestEq.errorCreate")
          : t("requestEq.errorCreate"),
        description,
      });
    },
    [addToast, isEditing, t]
  );

  const handleSubmit = useCallback(
    async (data: IUserFormData) => {
      removeNullKeys(data);

      try {
        const schema = Yup.object().shape({
          value: Yup.number().required("Valor é obrigatório"),
          name: Yup.string().required("Nome é obrigatório"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { value, name } = data;

        const payload = {
          name,
          user_id: user.id,
          value,
        };

        removeNullKeys(payload);
        let response: AxiosResponse;
        if (isEditing) {
          response = await api.put(`/configurations/${config?.id}`, payload);
        } else {
          response = await api.post("/configurations", payload);
        }

        handleClose([response.data]);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }

        console.log(err);
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      formatDate,
      generateErrorToast,
      handleClose,
      isEditing,
      config,
      t,
    ]
  );

  return (
    <Modal
      title={isEditing ? t("modalConfig.edit") : t("modalConfig.create")}
      visible={visible}
      destroyOnClose
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem
          name="name"
          required
          label={t("config.name")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        <FormItem
          name="value"
          required
          label={t("config.value")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default ConfigEditModal;
